<template>
  <b-tab lazy title-item-class="mb-1" :title-link-class="{ 'bg-danger': hasErrors, 'bg-warning': isDirty }">
    <template slot="title">
      <i v-if="isDirty || hasErrors" class="fa-solid fa-circle-exclamation" :class="{ 'fa-circle-exclamation text-white': hasErrors, 'fa-cloud-arrow-up text-black': isDirty }"></i> Permissions
    </template>

    <b-form-group label="User Role Permissions" label-size="lg" label-class="font-weight-bold pt-0">
      <b-card no-body>
        <b-tabs pills card vertical content-class="border-right-0 border-bottom-0 border-left-1 border-top-1">
          <b-tab lazy v-for="role in groupRoles" :title="role.name" :key="`group-roles-${role.id}`">

            <div class="d-flex mb-5">
              <b-button v-if="!hasFullAccess(role.id)" @click="setAccess(role.id)" variant="warning" class="ml-auto">
                <i class="fa-solid fa-unlock"></i> Give Full Access
              </b-button>

              <b-button v-if="role.permissions.length > 0" @click="revokeAllAccess(role.id)" variant="danger" class="ml-auto">
                <i class="fa-solid fa-ban"></i> Revoke Full Access
              </b-button>
            </div>

            <b-row>
              <b-col v-for="(permList, permType) in permissions" :key="permType">
                <b-form-group :label="permType.replace('_', ' ').toUpperCase()">
                  <b-button size="sm" variant="outline-primary" class="mb-2" @click="togglePermissionType(role.id, permType)">
                    Toggle All
                  </b-button>
                  <b-input-group v-for="permission in permList" :key="`${permType}-${permission.id}`">
                    <b-form-input class="mb-1" size="sm" disabled type="text" :value="permission.name" />
                    <b-input-group-append>
                      <b-button size="sm" class="mb-1" variant="light" @click="setPermission(role.id, permission)">
                        <i :class="{ 'text-secondary fa-regular fa-ban': !hasPermission(role.id, permission), 'text-success fa-solid fa-check': hasPermission(role.id, permission) }"></i>
                      </b-button>
                    </b-input-group-append>
                  </b-input-group>
                </b-form-group>
              </b-col>
            </b-row>
          </b-tab>
        </b-tabs>
      </b-card>
    </b-form-group>
  </b-tab>
</template>

<script>
import FormExtension from "@/core/components/FormExtension"
import { mapGetters, mapState, mapActions } from "vuex"

export default FormExtension.extend({
  name: 'user-permissions',
  computed: {
    ...mapGetters('auth', ['getPermissions', 'getRoles']),
    permissions() {
      return this.getPermissions()
    },
    groupRoles() {
      return this.getRoles()
    }
  },
  methods: {
    ...mapActions("auth", ["getFrontendPermissions", 'getFrontendRoles', 'updateFrontendRoles']),

    hasPermission(id, permission) {
      const role = this.groupRoles.find(role => role.id === id)
      return role ? role.permissions.some(p => p.id === permission.id) : false
    },

    async setPermission(id, permission) {
      const role = this.groupRoles.find(role => role.id === id)
      if (!role) return

      const currentPermissions = new Set(role.permissions.map(p => p.id))
      if (currentPermissions.has(permission.id)) {
        currentPermissions.delete(permission.id)
      } else {
        currentPermissions.add(permission.id)
      }

      await this.updateFrontendRoles({ id, data: { permission_ids: [...currentPermissions] } })
    },

    hasFullAccess(id) {
      const role = this.groupRoles.find(role => role.id === id)
      if (!role || role.permissions.length === 0) return false

      const allIds = Object.values(this.permissions).flatMap(items => items.map(item => item.id))
      const roleIds = role.permissions.map(p => p.id)

      return allIds.every(id => roleIds.includes(id)) && roleIds.length === allIds.length
    },

    async setAccess(id) {
      const allIds = Object.values(this.permissions).flatMap(items => items.map(item => item.id))
      await this.updateFrontendRoles({ id, data: { permission_ids: allIds } })
    },

    async revokeAllAccess(roleId) {
      await this.updateFrontendRoles({ id: roleId, data: { permission_ids: [] } })
    },

    async togglePermissionType(roleId, type) {
      const role = this.groupRoles.find(role => role.id === roleId)
      if (!role) return

      const permissionSet = new Set(role.permissions.map(p => p.id))
      const targetPermissions = this.permissions[type] || []
      const allHave = targetPermissions.every(p => permissionSet.has(p.id))

      let newPermissions
      if (allHave) {
        newPermissions = role.permissions.filter(p => !targetPermissions.some(tp => tp.id === p.id))
      } else {
        const permissionMap = new Map(role.permissions.map(p => [p.id, p]))
        targetPermissions.forEach(p => permissionMap.set(p.id, p))
        newPermissions = Array.from(permissionMap.values())
      }

      const newPermissionIds = newPermissions.map(p => p.id)
      await this.updateFrontendRoles({ id: roleId, data: { permission_ids: newPermissionIds } })
    }
  },
  async mounted() {
    await this.getFrontendPermissions()
    await this.getFrontendRoles()
  }
})
</script>